<script lang="ts">
	import { untrack } from 'svelte'
	import { flip } from 'svelte/animate'
	import type { ActionReturn } from 'svelte/action'
	import {
		dragHandle,
		dragHandleZone,
		SHADOW_ITEM_MARKER_PROPERTY_NAME,
		SOURCES,
		TRIGGERS,
		type DndEvent,
		type DndZoneAttributes,
		type Options as DndZoneOptions
	} from 'svelte-dnd-action'
	import {
		Checkbox,
		CheckboxSize,
		Icon,
		LoadingChasing,
		ContextMenu,
		Tooltip
	} from '@repo/commons/components'
	import Pagination from './Pagination.svelte'
	import type { TableColumn, TableData } from '@repo/commons/models'
	import type { ContextMenuItem, ContextMenuProps } from '@repo/commons/models'

	/** Emitted when page or page size changes so parent can fetch new data (e.g. server-side pagination). */
	export type PageChangeDetail = { page: number; pageSize: number }

	interface Props<T extends TableData = TableData> {
		data: T[]
		columns: TableColumn<T>[]
		sortable?: boolean
		paginated?: boolean
		pageSize?: number
		/** Total item count when using server-side pagination. If not set, uses data.length (client-side). */
		totalItems?: number
		/** Current page (0-based) for controlled mode. If not set, component manages page internally. */
		page?: number
		/** Called when user changes page or page size; use for fetching next page data. */
		onPageChange?: (detail: PageChangeDetail) => void
		scrollToTopOnPageChange?: boolean
		className?: string
		emptyText?: string
		clickableRows?: boolean
		onRowClick?: (row: T, index: number) => void
		selectable?: boolean
		selectedRows?: T[]
		onSelectionChange?: (selectedRows: T[]) => void
		isRowSelectable?: (row: T) => boolean
		getRowId?: (row: T) => unknown
		loading?: boolean
		defaultSort?: {
			column: string
			direction: 'asc' | 'desc'
		}
		contextMenuOptions?: ((row: T, index: number) => ContextMenuItem[]) | null
		horizontalScroll?: boolean
		minColumnWidth?: string
		pinEdgeColumns?: boolean
		reorderable?: boolean
		onReorder?: (data: T[]) => void
	}

	let {
		data = [],
		columns = [],
		sortable = true,
		paginated = false,
		pageSize = 10,
		totalItems: totalItemsProp,
		page: pageProp,
		onPageChange,
		className = '',
		emptyText = 'No data found',
		clickableRows = false,
		onRowClick,
		selectable = false,
		selectedRows = [],
		onSelectionChange,
		isRowSelectable = undefined,
		getRowId = undefined,
		loading = false,
		defaultSort,
		contextMenuOptions = null,
		horizontalScroll = false,
		minColumnWidth = '160px',
		pinEdgeColumns = false,
		reorderable = false,
		onReorder
	}: Props = $props()

	const getColumnStyle = (column: { width?: string; maxWidth?: string }): string => {
		if (column.width) return `max-width: ${column.width}; width: ${column.width}`
		if (column.maxWidth) return `max-width: ${column.maxWidth}`
		return ''
	}

	const isServerSide = $derived(totalItemsProp !== undefined && totalItemsProp !== null)
	const isControlledPage = $derived(pageProp !== undefined && pageProp !== null)

	let sortColumn = $state<string | null>(defaultSort?.column ?? null)
	let sortDirection = $state<'asc' | 'desc'>(defaultSort?.direction ?? 'asc')
	let internalPage = $state(0)
	let itemsPerPage = $state(pageSize)

	const currentPage = $derived(isControlledPage ? Math.max(0, pageProp!) : internalPage)

	let internalSelectedRows = $state(selectedRows || [])
	let contextMenuProps: ContextMenuProps | null = $state(null)

	$effect(() => {
		internalSelectedRows = selectedRows || []
	})

	// When data changes, drop any selected rows that are no longer selectable.
	// Uses untrack to read internalSelectedRows without creating a circular dependency.
	$effect(() => {
		if (!isRowSelectable) return

		const freshDataMap = new Map(data.map((row) => [getRowId?.(row), row]))
		const currentSelection = untrack(() => internalSelectedRows)

		const filtered = currentSelection.filter((selectedRow) => {
			const rowId = getRowId?.(selectedRow)
			const freshRow = freshDataMap.get(rowId)
			// Only re-check rows present in the current data; rows on other pages stay selected
			return freshRow !== undefined ? isSelectableRow(freshRow) : true
		})

		if (filtered.length !== currentSelection.length) {
			internalSelectedRows = filtered
			onSelectionChange?.(filtered)
		}
	})

	const sortedData = $derived.by(() => {
		if (!sortable || !sortColumn) return data

		return [...data].sort((a, b) => {
			const aVal = a[sortColumn as string]
			const bVal = b[sortColumn as string]

			let comparison = 0

			if (aVal == null && bVal == null) return 0
			if (aVal == null) return 1
			if (bVal == null) return -1

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				comparison = aVal - bVal
			} else if (Array.isArray(aVal) && Array.isArray(bVal)) {
				comparison = aVal.length - bVal.length
			} else {
				comparison = String(aVal).localeCompare(String(bVal))
			}

			return sortDirection === 'desc' ? -comparison : comparison
		})
	})

	const totalCount = $derived.by(() => {
		if (!paginated) return sortedData.length
		return isServerSide ? totalItemsProp! : sortedData.length
	})

	const paginatedData = $derived.by(() => {
		if (!paginated) return sortedData
		if (isServerSide) return sortedData
		const start = currentPage * itemsPerPage
		return sortedData.slice(start, start + itemsPerPage)
	})

	const totalPages = $derived.by(() => {
		if (!paginated) return 1
		return Math.max(1, Math.ceil(totalCount / itemsPerPage))
	})

	const REORDER_ANIMATION_MS = 100

	let tableElement = $state<HTMLTableElement | null>(null)
	let tableBodyElement = $state<HTMLTableSectionElement | null>(null)

	const sizeDraggedRowToColumns = (draggedEl: HTMLElement | undefined): void => {
		if (!draggedEl) return

		const headerCells = tableElement?.querySelectorAll('thead th')
		if (!headerCells?.length) return

		Array.from(draggedEl.children).forEach((cell, index) => {
			const header = headerCells[index] as HTMLElement | undefined
			if (!header) return

			const element = cell as HTMLElement
			const { width } = header.getBoundingClientRect()

			element.style.boxSizing = 'border-box'
			element.style.width = `${width}px`
			element.style.minWidth = `${width}px`
			element.style.maxWidth = `${width}px`
		})
	}

	type DndRow = {
		id: string
		row: TableData
		[SHADOW_ITEM_MARKER_PROPERTY_NAME]?: boolean
	}

	type ConditionalDragHandleZoneOptions = {
		enabled: boolean
		zoneOptions: DndZoneOptions<DndRow>
	}

	type DragHandleZoneInstance = {
		update?: (options: DndZoneOptions<DndRow>) => void
		destroy?: VoidFunction
	}

	const conditionalDragHandleZone = (
		node: HTMLElement,
		initialOptions: ConditionalDragHandleZoneOptions
	): ActionReturn<ConditionalDragHandleZoneOptions, DndZoneAttributes<DndRow>> => {
		let zone: DragHandleZoneInstance | undefined

		const destroy = (): void => {
			zone?.destroy?.()
			zone = undefined
		}

		const update = ({ enabled, zoneOptions }: ConditionalDragHandleZoneOptions): void => {
			if (!enabled) {
				destroy()
				return
			}

			if (zone) zone.update?.(zoneOptions)
			else zone = dragHandleZone<DndRow>(node, zoneOptions)
		}

		update(initialOptions)

		return { update, destroy }
	}

	let dragRows = $state<DndRow[] | null>(null)
	let draggedRow = $state<DndRow | null>(null)
	let isPointerInsideReorderZone = $state(false)

	const baseRows: DndRow[] = $derived(
		paginatedData.map((row, index) => ({
			id: String(getRowId?.(row) ?? index),
			row
		}))
	)

	const dndRows: DndRow[] = $derived(reorderable && dragRows ? dragRows : baseRows)

	const clearReorderDrag = (): void => {
		dragRows = null
		draggedRow = null
		isPointerInsideReorderZone = false
	}

	const handleReorderConsider = (e: CustomEvent<DndEvent<DndRow>>): void => {
		if (!reorderable) return

		const { info, items } = e.detail
		if (info.source === SOURCES.KEYBOARD) {
			dragRows = items
			return
		}

		if (info.trigger !== TRIGGERS.DRAG_STARTED) return

		draggedRow = baseRows.find(({ id }) => id === String(info.id)) ?? null
		dragRows = items
		isPointerInsideReorderZone = true
	}

	const updateReorderPointerPosition = (clientX: number, clientY: number): void => {
		if (!reorderable || !draggedRow || !dragRows || !tableBodyElement) return

		const { bottom, height, left, right, top } = tableBodyElement.getBoundingClientRect()
		isPointerInsideReorderZone =
			clientX >= left && clientX <= right && clientY >= top && clientY <= bottom
		if (!isPointerInsideReorderZone) return

		const shadowRow = dragRows.find((item) => item[SHADOW_ITEM_MARKER_PROPERTY_NAME])
		if (!shadowRow || !height || !baseRows.length) return

		const remainingRows = baseRows.filter(({ id }) => id !== draggedRow?.id)
		const rowHeight = height / baseRows.length
		const targetIndex = Math.min(
			remainingRows.length,
			Math.max(0, Math.floor((clientY - top) / rowHeight))
		)
		const nextRows = [...remainingRows]
		nextRows.splice(targetIndex, 0, shadowRow)

		if (nextRows.every(({ id }, index) => dragRows?.[index]?.id === id)) return
		dragRows = nextRows
	}

	const handleReorderPointerMove = ({ clientX, clientY }: MouseEvent): void => {
		updateReorderPointerPosition(clientX, clientY)
	}

	const handleReorderTouchMove = ({ touches }: TouchEvent): void => {
		const [touch] = touches
		if (!touch) return

		updateReorderPointerPosition(touch.clientX, touch.clientY)
	}

	const handleReorderFinalize = (e: CustomEvent<DndEvent<DndRow>>): void => {
		if (!reorderable) return

		const { info } = e.detail
		const droppedOutside =
			info.trigger === TRIGGERS.DROPPED_OUTSIDE_OF_ANY ||
			(info.source === SOURCES.POINTER && !isPointerInsideReorderZone)

		if (droppedOutside) {
			clearReorderDrag()
			return
		}

		const activeDraggedRow = draggedRow
		const finalizedRows =
			activeDraggedRow && dragRows
				? dragRows.map((item) => (item[SHADOW_ITEM_MARKER_PROPERTY_NAME] ? activeDraggedRow : item))
				: e.detail.items

		clearReorderDrag()
		onReorder?.(finalizedRows.map(({ row }) => row))
	}

	const showsSelectionColumn = $derived(selectable && (data.length > 0 || loading))

	const isSelectableRow = (row: TableData): boolean => isRowSelectable?.(row) ?? true

	const currentPageSelectableRows = $derived.by(() =>
		paginatedData.filter((row) => isSelectableRow(row))
	)

	const currentPageSelectableRowIds = $derived.by(() =>
		currentPageSelectableRows.map((row) => getRowId?.(row))
	)

	const selectedRowIds = $derived.by(() => internalSelectedRows.map((row) => getRowId?.(row)))

	const currentPageSelectedRows = $derived.by(() =>
		currentPageSelectableRows.filter((row) => {
			const rowId = getRowId?.(row)

			return selectedRowIds.includes(rowId)
		})
	)

	const isAllCurrentPageSelected = $derived.by(() => {
		const selectableCount = currentPageSelectableRows.length

		if (selectableCount === 0) return false

		return currentPageSelectedRows.length === selectableCount
	})

	const isSomeCurrentPageSelected = $derived.by(() => {
		const selectedCount = currentPageSelectedRows.length

		return selectedCount > 0 && selectedCount < currentPageSelectableRows.length
	})

	function handleSort(columnKey: string) {
		if (loading) return

		const column = columns.find((col) => col.key === columnKey)
		if (!sortable || !column || column.sortable === false) return

		if (sortColumn === columnKey) {
			if (sortDirection === 'desc') {
				sortColumn = null
				sortDirection = 'asc'
				return
			}
			sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
		} else {
			sortColumn = columnKey
			sortDirection = 'asc'
		}
	}

	function handleRowClick(row: TableData, index: number) {
		if (loading) return

		if (clickableRows && typeof onRowClick === 'function') {
			onRowClick(row, index)
		}
	}

	function isRowSelected(row: TableData): boolean {
		const rowId = getRowId?.(row)
		return selectedRowIds.includes(rowId)
	}

	function handleRowSelection(row: TableData, checked: boolean) {
		if (loading || !isSelectableRow(row)) return

		const rowId = getRowId?.(row)

		if (checked) {
			// Add row to selection if not already selected
			if (!selectedRowIds.includes(rowId)) {
				internalSelectedRows = [...internalSelectedRows, row]
			}
		} else {
			// Remove row from selection
			internalSelectedRows = internalSelectedRows.filter((selectedRow) => {
				const selectedRowId = getRowId?.(selectedRow)
				return selectedRowId !== rowId
			})
		}

		if (onSelectionChange) {
			onSelectionChange(internalSelectedRows)
		}
	}

	function handleHeaderCheckboxChange(checked: boolean) {
		if (loading) return

		if (checked) {
			// Add all selectable current-page rows that aren't already selected
			const newSelections = currentPageSelectableRows.filter((row) => {
				const rowId = getRowId?.(row)
				return !selectedRowIds.includes(rowId)
			})
			internalSelectedRows = [...internalSelectedRows, ...newSelections]
		} else {
			// Remove all selectable current-page rows from selection
			const currentPageIds = currentPageSelectableRowIds
			internalSelectedRows = internalSelectedRows.filter((selectedRow) => {
				const selectedRowId = getRowId?.(selectedRow)
				return !currentPageIds.includes(selectedRowId)
			})
		}

		if (onSelectionChange) {
			onSelectionChange(internalSelectedRows)
		}
	}

	const escapeHtml = (text: string): string =>
		text
			.replaceAll('&', '&amp;')
			.replaceAll('<', '&lt;')
			.replaceAll('>', '&gt;')
			.replaceAll('"', '&quot;')
			.replaceAll("'", '&#39;')

	function getCellContent(column: TableColumn, row: TableData): string {
		const value = row[column.key]

		if (column.render) {
			return column.render(value, row)
		}

		return escapeHtml(Array.isArray(value) ? value.join(', ') : String(value ?? ''))
	}

	function getSortDirection(columnKey: string): string {
		if (sortColumn !== columnKey) return ''
		return sortDirection
	}

	function openContextMenu(event: MouseEvent, row: TableData, index: number) {
		if (!contextMenuOptions) return

		event.preventDefault()
		event.stopPropagation()

		// Use page coordinates directly since ContextMenu is portaled to body
		contextMenuProps = {
			x: event.pageX,
			y: event.pageY,
			items: contextMenuOptions(row, index) ?? [],
			onClose: () => {
				contextMenuProps = null
			}
		}
	}

	function closeContextMenu() {
		contextMenuProps = null
	}

	function goToPage(page: number) {
		if (loading) return
		const next = Math.max(0, Math.min(page, totalPages - 1))
		if (next === currentPage) return
		if (!isControlledPage) internalPage = next
		onPageChange?.({ page: next, pageSize: itemsPerPage })
	}

	// Clamp to valid page when total shrinks (e.g. filters); parent can react to data length for server-side
	$effect(() => {
		if (currentPage >= totalPages && totalPages > 0 && !isControlledPage) {
			internalPage = Math.max(0, totalPages - 1)
		}
	})

	// Close context menu on outside click
	$effect(() => {
		if (contextMenuProps) {
			const handler = () => closeContextMenu()
			document.addEventListener('click', handler)
			return () => {
				document.removeEventListener('click', handler)
			}
		}
	})

	$effect(() => {
		if (!reorderable || !draggedRow) return

		window.addEventListener('mousemove', handleReorderPointerMove)
		window.addEventListener('touchmove', handleReorderTouchMove, { passive: true })

		return () => {
			window.removeEventListener('mousemove', handleReorderPointerMove)
			window.removeEventListener('touchmove', handleReorderTouchMove)
		}
	})
</script>

<div class="data-table {className}" class:loading>
	<div class="table-container" class:horizontal-scroll={horizontalScroll}>
		<table
			bind:this={tableElement}
			class="table"
			class:horizontal-scroll={horizontalScroll}
			class:pin-edges={pinEdgeColumns}
			style:--column-min-width={minColumnWidth}
			style:--pinned-start-offset={showsSelectionColumn ? '48px' : '0px'}
		>
			<thead>
				<tr class="table-header-row">
					{#if reorderable && (data.length > 0 || loading)}
						<th class="table-header-cell drag-handle-column" aria-hidden="true"></th>
					{/if}
					{#if showsSelectionColumn}
						<th class="table-header-cell checkbox-column" class:pinned-start={pinEdgeColumns}>
							<div class="header-content">
								{#if loading}
									<div class="skeleton skeleton-checkbox"></div>
								{:else}
									<Checkbox
										size={CheckboxSize.SMALL}
										checked={isAllCurrentPageSelected}
										indeterminate={isSomeCurrentPageSelected}
										disabled={currentPageSelectableRows.length === 0}
										onchange={(checked) => handleHeaderCheckboxChange(checked)}
									/>
								{/if}
							</div>
						</th>
					{/if}
					{#each columns as column, columnIndex}
						<th
							class="table-header-cell"
							class:cell-clickable={!!column.sortable && !loading}
							class:disabled={loading}
							class:pinned-start={pinEdgeColumns && columnIndex === 0}
							class:pinned-end={pinEdgeColumns && columnIndex === columns.length - 1}
							style={getColumnStyle(column)}
							onclick={() => handleSort(column.key)}
						>
							<div class="header-content">
								<span class="header-label-group">
									{#if column.label}
										<span class="header-label">{column.label}</span>
									{/if}
									{#if column.labelIcon}
										{#if column.labelIconLabel}
											<Tooltip
												text={column.labelIconLabel}
												position="top"
												tooltipSize="small"
												delay={100}
												offset={8}
											>
												<span
													class="header-label-icon"
													onpointerdown={(event) => event.stopPropagation()}
												>
													<Icon
														name={column.labelIcon}
														size="sm"
														aria-label={column.labelIconLabel}
													/>
												</span>
											</Tooltip>
										{:else}
											<span class="header-label-icon">
												<Icon name={column.labelIcon} size="sm" />
											</span>
										{/if}
									{/if}
								</span>
								{#if sortable && !!column.sortable && !loading}
									{@const columnSortDirection = getSortDirection(column.key)}
									<span class="sort-icon">
										{#if Icon}
											{#if columnSortDirection === 'asc'}
												<Icon name="caretUp" size="sm" />
											{:else if columnSortDirection === 'desc'}
												<Icon name="caretDown" size="sm" />
											{:else}
												<Icon name="sort" size="sm" />
											{/if}
										{/if}
									</span>
								{/if}
							</div>
						</th>
					{/each}
				</tr>
			</thead>
			<tbody
				bind:this={tableBodyElement}
				class="table-body"
				use:conditionalDragHandleZone={{
					enabled: reorderable,
					zoneOptions: {
						items: dndRows,
						flipDurationMs: REORDER_ANIMATION_MS,
						transformDraggedElement: sizeDraggedRowToColumns,
						useCursorForDetection: true,
						dropTargetStyle: {}
					}
				}}
				onconsider={handleReorderConsider}
				onfinalize={handleReorderFinalize}
			>
				{#if loading}
					<tr>
						<td
							colspan={columns.length + (selectable ? 1 : 0) + (reorderable ? 1 : 0)}
							class="loading-spinner-cell"
						>
							<div class="spinner-container">
								<LoadingChasing size="30" />
							</div>
						</td>
					</tr>
				{:else if dndRows.length > 0}
					{#each dndRows as dndRow, pageIndex (dndRow.id)}
						{@const row = dndRow.row}
						{@const rowIndexInData = data.findIndex((r) =>
							getRowId ? getRowId(r) === getRowId(row) : r === row
						)}
						{@const actualIndex =
							rowIndexInData !== -1
								? rowIndexInData
								: paginated
									? currentPage * itemsPerPage + pageIndex
									: pageIndex}
						<tr
							class="table-row"
							class:cell-clickable={!!clickableRows}
							class:row-selected={isRowSelected(row)}
							animate:flip={{ duration: reorderable ? REORDER_ANIMATION_MS : 0 }}
							onclick={() => handleRowClick(row, actualIndex)}
							oncontextmenu={(e) => openContextMenu(e, row, actualIndex)}
						>
							{#if reorderable}
								<td class="table-cell drag-handle-cell" onclick={(e) => e.stopPropagation()}>
									<span use:dragHandle class="drag-handle" aria-label="Drag to reorder">
										<Icon name="draggable" size="sm" class="text-[#002C4D80]" />
									</span>
								</td>
							{/if}
							{#if selectable}
								<td
									class="table-cell checkbox-column"
									class:pinned-start={pinEdgeColumns}
									onclick={(e) => e.stopPropagation()}
								>
									<Checkbox
										size={CheckboxSize.SMALL}
										checked={isRowSelected(row)}
										disabled={!isSelectableRow(row)}
										onchange={(checked) => handleRowSelection(row, checked)}
									/>
								</td>
							{/if}
							{#each columns as column, columnIndex}
								<td
									class="table-cell"
									class:pinned-start={pinEdgeColumns && columnIndex === 0}
									class:pinned-end={pinEdgeColumns && columnIndex === columns.length - 1}
									style={getColumnStyle(column)}
								>
									{#if column.component}
										{@const props = {
											row,
											index: actualIndex,
											...(column.componentProps || {}),
											key: column.key
										}}
										{#key `${getRowId?.(row) ?? actualIndex}-${column.key}`}
											<column.component {...props} />
										{/key}
									{:else}
										{@html getCellContent(column, row)}
									{/if}
								</td>
							{/each}
						</tr>
					{/each}
				{:else}
					<tr>
						<td
							colspan={columns.length + (selectable ? 1 : 0) + (reorderable ? 1 : 0)}
							class="empty-state-cell"
						>
							<div class="empty-state-content">
								<Icon name="document" size="xl" />
								<p class="text-base mt-4">{emptyText}</p>
							</div>
						</td>
					</tr>
				{/if}
			</tbody>
		</table>
	</div>

	{#if paginated && totalCount > 0}
		<Pagination
			{currentPage}
			{totalPages}
			{totalCount}
			{itemsPerPage}
			{loading}
			onPageChange={(detail) => goToPage(detail.page)}
		/>
	{/if}
</div>

{#if contextMenuProps && contextMenuProps.items.length > 0}
	<ContextMenu
		x={contextMenuProps.x}
		y={contextMenuProps.y}
		items={contextMenuProps.items}
		onClose={() => closeContextMenu()}
	/>
{/if}

<style>
	.data-table {
		@apply w-full;
		border-collapse: collapse;
	}

	.data-table.loading {
		pointer-events: none;
		user-select: none;
	}

	.table-container {
		@apply overflow-x-auto;
		max-width: 100%;
	}

	.table.horizontal-scroll {
		width: max-content;
		min-width: 100%;
	}

	.table {
		@apply w-full divide-y;
	}

	.table-header-row {
		@apply h-[48px];
	}

	.table-header-cell {
		@apply text-left text-xs font-medium text-gray-500 h-[48px];
		padding: 0 16px;
		user-select: none;
		border-bottom: 1px solid #003a661a;
		border-top: 1px solid #003a661a;
	}

	.table-header-cell.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.checkbox-column {
		width: 48px;
		padding: 0 12px !important;
	}

	.drag-handle-column {
		width: 32px;
		padding: 0 !important;
	}

	.drag-handle-cell {
		width: 32px;
		padding: 0 !important;
		text-align: center;
	}

	.drag-handle {
		@apply inline-flex items-center justify-center;

		width: 32px;
		height: 32px;
		cursor: grab;
	}

	.drag-handle:active {
		cursor: grabbing;
	}
	.cell-clickable {
		cursor: pointer;
	}

	.header-content {
		@apply flex items-center gap-1;
		min-width: 0;
	}

	.header-label-icon {
		@apply inline-flex shrink-0 items-center;

		color: #007acccc;
	}

	.sort-icon {
		@apply flex items-center justify-center text-gray-400 w-6 h-6;
	}

	.table-body {
		@apply bg-white divide-y divide-gray-200;
	}

	.table-row {
		@apply transition-colors h-[80px] text-[#001D33B2] font-normal;

		&.row-selected {
			@apply bg-[#0049800D];
		}
	}

	.table-row:not(.loading-row):hover {
		@apply bg-[#0049800D];
	}

	.loading-row {
		pointer-events: none;
	}

	.table-cell {
		@apply px-6 py-4 whitespace-pre-line text-base;
		padding: 12px 16px;
		text-align: left;
		border-bottom: 1px solid #003a661a;
		overflow-wrap: anywhere;
		overflow: hidden;
	}

	:global(.table.horizontal-scroll) .table-cell:not(.pinned-start):not(.pinned-end) {
		min-width: var(--column-min-width, 160px);
	}

	.table.pin-edges {
		border-collapse: separate;
		border-spacing: 0;
	}

	.table.pin-edges > tbody {
		border-top-width: 0;
	}

	.table.pin-edges .pinned-start,
	.table.pin-edges .pinned-end {
		@apply transition-colors;

		position: sticky;
		z-index: 1;
		background-color: var(--pinned-cell-bg, #fff);
	}

	.table.pin-edges .table-header-cell.pinned-start,
	.table.pin-edges .table-header-cell.pinned-end {
		z-index: 2;
	}

	.table.pin-edges .checkbox-column.pinned-start {
		left: 0;
	}

	.table.pin-edges .table-cell.pinned-start:not(.checkbox-column),
	.table.pin-edges .table-header-cell.pinned-start:not(.checkbox-column) {
		left: var(--pinned-start-offset, 0px);
	}

	.table.pin-edges .pinned-end {
		right: 0;
	}

	.table.pin-edges .table-row:hover,
	.table.pin-edges .table-row.row-selected {
		--pinned-cell-bg: #f2f6f9;
	}

	.table-cell:has([style*='max-width']) {
		white-space: normal;
	}

	.header-label-group {
		@apply flex items-center;

		gap: 8px;
		flex: 1;
		min-width: 0;
	}

	.header-label {
		@apply text-[#001D33B2] font-semibold;
		font-size: 14px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
	}

	.empty-state-cell {
		@apply px-6 py-12 text-center text-gray-500;
	}

	.empty-state-content {
		@apply flex flex-col items-center;
	}

	.empty-state-icon {
		@apply w-12 h-12 text-gray-300 mb-4;
	}

	.loading-spinner-cell {
		@apply py-12 text-center;
	}

	.spinner-container {
		@apply flex justify-center items-center;
	}
</style>
